import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import styled from 'styled-components';

import Grid from 'LegacyWeployDesignLanguage/Grid';

import { GET_INVOICE_SUMMARIES, GET_PAYMENT_TERMS, GET_INVOICE_TOTALS } from './graphql/queries';
import { PolicyContext, UPDATE_ORGANISATION_BILLING_ACCOUNT } from '../PolicyProvider';
import { EDIT_BILLING_ACCOUNT_PATH, getExternalPath } from '../pathHelpers';
import { getPaymentMethods } from './util';
import formatCurrency from '../../../utils/Currency';

import SkillInvoiceSummary from '../../../components/weployer/timesheets/SkillInvoiceSummary';
import PaymentConfirmation from '../../../components/weployer/timesheets/PaymentConfirmation';
import PaymentTermsSelection from '../../../components/weployer/timesheets/PaymentTermSelection';
import CouponSelection from '../../../components/weployer/timesheets/CouponSelection';

const StyledSpan = styled.span`
  ${props => props.theme.typography.body1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledPaymentTermText = styled.p`
  ${props => props.theme.typography.body1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledRow = styled.div`
  ${props => props.theme.typography.subtitle1};
  padding: ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledPaymentMethodHeader = styled.h6`
  padding-top: ${props => props.theme.spacing[2]};
`;

const StyledHorizontalRule = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`;

const StyledA = styled.a`
    ${props => props.theme.typography.subtitle1};
    color: ${props => props.theme.colors.text.primary};
    font-weight: 600;
`;

const StyledGridNoTopPadding = styled(Grid)`
  &.MuiGrid-item {
    padding-top: ${props => props.theme.spacing[0]};
  }
`;

const StyledGridReducedBottomPadding = styled(Grid)`
  &.MuiGrid-item {
    padding-bottom: ${props => props.theme.spacing[1]};
  }
`;

const Row = ({ description, amount, currency }) => (
  <StyledRow>
    <Grid
      container
      justify="space-between"
      alignItems="center"
    >
      <Grid item xs={6} lg={8}>{description}</Grid>
      <Grid item xs={6} lg={4}>
        <Grid container justify="flex-end">
          {formatCurrency(amount, currency)}
        </Grid>
      </Grid>
    </Grid>
  </StyledRow>
);

const PaymentSummary = ({
  selectedPaymentTerm,
  timesheetIds,
  setPaymentMethods,
  onConfirmButtonClick,
  onPaymentTermSelect,
  onBillingDetailsClick,
  selectedCoupon,
  onCouponSelect,
  onEmailClick,
  ...rest
}) => {
  const { data: paymentTermsData, loading: paymentTermsLoading } = useQuery(GET_PAYMENT_TERMS);
  const { loading, data } = useQuery(GET_INVOICE_SUMMARIES, {
    variables: { timesheetIds },
  });
  const { loading: invoiceTotalsLoading, data: invoiceTotalsData } = useQuery(GET_INVOICE_TOTALS, {
    variables: { timesheetIds, paymentTermId: selectedPaymentTerm.id, redeemedCouponId: selectedCoupon.id },
  });

  const { actions } = useContext(PolicyContext);

  const canUpdateOrganisationBillingAccount = () => (
    actions.includes(UPDATE_ORGANISATION_BILLING_ACCOUNT)
  );

  const renderPaymentTermsSelection = () => {
    if (!data.weployer.paymentTerm && !paymentTermsLoading) {
      return (
        <PaymentTermsSelection
          onPaymentTermChange={onPaymentTermSelect}
          paymentTermId={selectedPaymentTerm.id}
          paymentTerms={paymentTermsData.referenceData.paymentTerms}
          editBillingPath={getExternalPath({ pathName: EDIT_BILLING_ACCOUNT_PATH })}
          showEditBillingPath={canUpdateOrganisationBillingAccount()}
        />
      );
    }
    return null;
  };

  const renderCouponSelection = () => {
    if (!loading && data.weployer.availableCoupons.length) {
      return (
        <CouponSelection
          onSelect={onCouponSelect}
          selectedCouponId={selectedCoupon.id}
          coupons={data.weployer.availableCoupons}
        />
      );
    }
    return null;
  };

  const renderInvoiceTotals = (paymentMethods, paymentTerm) => {
    if (!invoiceTotalsLoading && invoiceTotalsData) {
      const {
        weployer: {
          invoiceTotals: {
            couponGst,
            coupon,
            creditSurchargeFee,
            grandTotal,
            subtotalGst,
            subtotal,
            currency,
          },
        },
      } = invoiceTotalsData;
      const couponAdjustedGst = subtotalGst - couponGst;
      return (
        <>
          <h6>Invoice Summary</h6>
          <Row
            description="Subtotal"
            amount={subtotal}
            currency={currency}
          />
          { paymentTerm.chargePercentage > 0 &&
            <Row
              description={`Credit facility surcharge (${paymentTerm.chargePercentage}%)`}
              amount={creditSurchargeFee}
              currency={currency}
            />
          }
          { selectedCoupon.remainingCredit > 0 &&
            <Row
              description="Coupon"
              amount={coupon}
              currency={currency}
            />
          }
          <Row
            description="GST"
            amount={couponAdjustedGst}
            currency={currency}
          />
          <StyledHorizontalRule />
          <Row
            description="Total"
            amount={grandTotal}
            currency={currency}
          />
          <PaymentConfirmation
            onConfirmButtonClick={() => {
              setPaymentMethods(paymentMethods);
              onConfirmButtonClick();
            }}
            paymentTermId={paymentTerm.id}
            {...rest}
          />
        </>
      );
    }
    return null;
  };

  if (!loading) {
    const { weployer: { invoiceSummaries, paymentTerm: billingAccountPaymentTerm } } = data;
    const paymentMethods = getPaymentMethods(invoiceSummaries);
    const paymentTerm = billingAccountPaymentTerm || selectedPaymentTerm;
    const paymentTermCode = paymentTerm.code;

    return (
      <Grid
        container
        spacing={4}
      >
        <StyledGridReducedBottomPadding item xs={12} lg={8}>
          {invoiceSummaries.map(invoiceSummary => (
            <SkillInvoiceSummary
              skillInvoiceSummary={
                {
                  total: invoiceSummary.subtotal + invoiceSummary.subtotalGst,
                  ...invoiceSummary,
                }
              }
              key={invoiceSummary.skillId}
              {...rest}
            />
          ))}
          <StyledSpan>
            <span>If these timesheets don&apos;t look quite right, </span>
            <StyledA
              onClick={onEmailClick}
              href="mailto:hello@weployapp.com?Subject=Timesheet%20query"
              target="_top"
            >
              send an email
            </StyledA>
            <span> and we&apos;ll get to you as soon as we can</span>
          </StyledSpan>
          <StyledPaymentMethodHeader>Payment Method</StyledPaymentMethodHeader>
          <StyledSpan>{paymentMethods.join(' and ')}</StyledSpan>
          { renderCouponSelection() }
          {
            billingAccountPaymentTerm ?
              (
                <>
                  <StyledPaymentTermText>{ paymentTermCode.replace('_', ' ') } payment term</StyledPaymentTermText>
                  <StyledPaymentTermText>
                    {
                      canUpdateOrganisationBillingAccount()
                        ? (
                          <>
                            <span>You can adjust the payment terms for the organisation in </span>
                            <StyledA
                              onClick={onBillingDetailsClick}
                              href={getExternalPath({ pathName: EDIT_BILLING_ACCOUNT_PATH })}
                            >
                              Billing
                            </StyledA>
                          </>
                        )
                        : 'Admin users can adjust the payment terms for the organisation'
                    }
                  </StyledPaymentTermText>
                </>
              ) : renderPaymentTermsSelection()
          }
        </StyledGridReducedBottomPadding>
        <StyledGridNoTopPadding item xs={12} lg={4}>
          { renderInvoiceTotals(paymentMethods, paymentTerm) }
        </StyledGridNoTopPadding>
      </Grid>
    );
  }
  return null;
};

PaymentSummary.propTypes = {
  onBillingDetailsClick: PropTypes.func,
  onConfirmButtonClick: PropTypes.func.isRequired,
  onCouponSelect: PropTypes.func.isRequired,
  onEmailClick: PropTypes.func,
  onPaymentTermSelect: PropTypes.func.isRequired,
  selectedCoupon: PropTypes.object,
  selectedPaymentTerm: PropTypes.object,
  setPaymentMethods: PropTypes.func.isRequired,
  timesheetIds: PropTypes.arrayOf(PropTypes.string),
};

PaymentSummary.defaultProps = {
  onBillingDetailsClick: () => {},
  onEmailClick: () => {},
  selectedCoupon: { remainingCredit: 0, id: '', code: '' },
  selectedPaymentTerm: { chargePercentage: 0 },
  timesheetIds: [],
};

Row.propTypes = {
  description: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
};

export default PaymentSummary;
